import React, {useImperativeHandle, useRef} from 'react'
import NavModal from '@component/header/GlobalNavigation/MobileNavigation/NavModal/NavModal'
import WizButton from '@component/buttons/WizButton'
import {useTranslation} from '@lib/intl/useTranslations'
import {useRouter} from 'next/router'
import {useLoginStore} from '@store/LoginStore'
import {shallow} from 'zustand/shallow'
import MobileNavigationSubItem from '@component/header/GlobalNavigation/MobileNavigation/MobileNavigationSubItem/MobileNavigationSubItem'
import useLogin from '@hook/useLogin'
import MobileNavigationSpace from '@component/header/GlobalNavigation/MobileNavigation/MobileNavigationSpace/MobileNavigationSpace'
import ChangeCurrencyModal from '@feature/common/components/ChangeCurrencyModal/ChangeCurrencyModal'
import useChangeCurrency from '@hook/useCurrencyChange'
import MobileNavigationItem from '@component/header/GlobalNavigation/MobileNavigation/MobileNavigationItem/MobileNavigationItem'
import useDidMountEffect from '@hook/UseDidMountEffect'
import ChangeLanguageModal from '@feature/common/components/ChangeLanguageModal/ChangeLanguageModal'
import useLanguageChange from '@hook/useLanguageChange'
import {useLoginModalStore} from '@store/LoginModalStore'
import useDark from '@hook/useDark'
import ChangeThemeModal from '@feature/common/components/ChangeThemeModal/ChangeThemeModal'
import {DarkModeStore} from '@store/DarkmodeStore'
import {createLog} from '@util/logs'
import IconSearchMobile from '@svg/gnb/img_mo_search.svg'
import WizIcon from '@component/images/WizIcon'
import {
    getLinkUrl,
    URL_AI,
    URL_ANNOUNCE,
    URL_CHART,
    URL_COPY_PORTFOLIO,
    URL_LEADER_PORTFOLIO,
    URL_LEADERBOARD,
    URL_LEADERBOARD_COPY,
    URL_LEADERBOARD_LEAGUE,
    URL_MEME,
    URL_PORTFOLIO,
    URL_TIMELINE,
    URL_TOKEN,
} from '@constant/url'
import MobileNavigationTitle from '@component/header/GlobalNavigation/MobileNavigation/MobileNavigationTitle'
import {setUserProperty} from '@util/amplitude'
import NewTag from '@component/tags/NewTag'

interface IProps {
    onClickSearchModal?: () => void
}

const MobileNavigation = ({onClickSearchModal}: IProps, ref: any) => {
    const modalRef = useRef<any>()
    const t = useTranslation()
    const router = useRouter()
    const currentPath = router?.asPath
    const {id, logout} = useLoginStore(
        state => ({
            id: state.id,
            logout: state.logout,
        }),
        shallow,
    )

    const {isLogin} = useLogin()
    const changeCurrencyModalRef = useRef<any>()
    const changeLanguageModalRef = useRef<any>()
    const changeThemeModalRef = useRef<any>()
    const {getCurrentCurrencyOption} = useChangeCurrency()
    const {getCurrentLanguage} = useLanguageChange()
    const {isRenderDark} = useDark()
    const showLoginModal = useLoginModalStore(state => state.showLoginModal)
    const {isCheckDark} = DarkModeStore()

    useDidMountEffect(() => {
        setUserProperty('currency', getCurrentCurrencyOption()?.id || 'USD')
    }, [getCurrentCurrencyOption])

    useImperativeHandle(ref, () => ({
        show: () => {
            modalRef.current.show()
        },
        close: () => {
            modalRef.current.close()
        },
    }))

    const closeModal = () => {
        modalRef?.current?.close()
    }

    const onClickLogout = () => {
        logout()
        router.push('/leaderboard')
        closeModal()
    }

    const onClickItem = (page: string) => {
        createLog('event', 'navigation_mobile_click_nav_menu', {page})
        closeModal()
    }
    const onClickItemWithLoginCheck = (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>, page: string) => {
        if (!id) {
            showLoginModal()
            e?.preventDefault()
            e?.stopPropagation()
        }
        onClickItem(page)
    }

    return (
        <>
            <NavModal ref={modalRef} className={''}>
                <div className={'flex flex-col min-h-screen'}>
                    <div className={'absolute top-[22px] left-[20px] p-[4px]'}>
                        <WizIcon
                            className={`relative hover:bg-transparent01 dark:hover:bg-dark_transparent01 md:hover:bg-transparent fill-gray01 dark:fill-dark_gray01 cursor-pointer`}
                            onClick={() => {
                                createLog('event', 'navigation_notification_open_search_modal')
                                onClickSearchModal()
                                closeModal()
                            }}>
                            <IconSearchMobile className={`w-[20px] block`} />
                        </WizIcon>
                    </div>
                    {!isLogin() && (
                        <div className={'p-[10px] gap-x-[10px] flex items-center'}>
                            <WizButton
                                text={t('mobileNavigation.signIn')}
                                buttonType={'border'}
                                className={'w-full flex-1'}
                                onClick={() => {
                                    createLog('event', 'navigation_mobile_open_login_modal')
                                    modalRef?.current?.close()
                                    showLoginModal()
                                }}
                            />
                            <WizButton
                                text={t('mobileNavigation.getStarted')}
                                buttonType={'primary'}
                                className={'w-full flex-1'}
                                onClick={() => {
                                    createLog('event', 'navigation_mobile_open_login_modal')
                                    modalRef?.current?.close()
                                    showLoginModal()
                                }}
                            />
                        </div>
                    )}

                    {/* title - leaderboard */}
                    <MobileNavigationTitle
                        className={'border-none'}
                        link={getLinkUrl(URL_LEADERBOARD)}
                        title={t('mobileNavigation.LeaderBoard')}
                        onClick={() => onClickItem('leaderboard')}
                    />
                    <MobileNavigationSubItem
                        text={t('mobileNavigation.LeaderBoard.mainLeaderboard')}
                        link={getLinkUrl(URL_LEADERBOARD)}
                        onClick={() => onClickItem('leaderboard')}
                    />
                    <MobileNavigationSubItem
                        text={t('mobileNavigation.LeaderBoard.copyLeader')}
                        link={getLinkUrl(URL_LEADERBOARD_COPY)}
                        onClick={() => onClickItem('leaderboard_copy')}
                    />
                    <MobileNavigationSubItem
                        className={'border-b border-gray07 dark:border-dark_gray07'}
                        text={t('mobileNavigation.LeaderBoard.league')}
                        link={getLinkUrl(URL_LEADERBOARD_LEAGUE)}
                        onClick={() => onClickItem('leaderboard_league')}
                    />

                    {/* title - user portfolio */}
                    <MobileNavigationTitle
                        className={'border-none'}
                        link={getLinkUrl(URL_PORTFOLIO(id))}
                        title={t('mobileNavigation.portfolio')}
                        onClick={e => onClickItemWithLoginCheck(e, 'portfolio')}
                    />
                    <MobileNavigationSubItem
                        text={t('mobileNavigation.portfolio.portfolio')}
                        link={getLinkUrl(URL_PORTFOLIO(id))}
                        onClick={e => onClickItemWithLoginCheck(e, 'portfolio')}
                    />
                    <MobileNavigationSubItem
                        text={t('mobileNavigation.portfolio.copyfolio')}
                        link={getLinkUrl(URL_COPY_PORTFOLIO(id))}
                        onClick={e => onClickItemWithLoginCheck(e, 'copy_portfolio')}
                    />
                    <MobileNavigationSubItem
                        className={'border-b border-gray07 dark:border-dark_gray07'}
                        text={t('mobileNavigation.portfolio.leaderfolio')}
                        link={getLinkUrl(URL_LEADER_PORTFOLIO(id))}
                        onClick={e => onClickItemWithLoginCheck(e, 'leader_portfolio')}
                    />

                    {/* title - token */}
                    <MobileNavigationTitle
                        link={getLinkUrl(URL_TOKEN)}
                        title={t('mobileNavigation.lot.token')}
                        onClick={() => onClickItem('token')}
                    />

                    {/* title - community */}
                    <MobileNavigationTitle
                        className={'border-none'}
                        link={getLinkUrl(URL_TIMELINE)}
                        title={t('mobileNavigation.community')}
                        onClick={() => onClickItem('community')}
                    />
                    <MobileNavigationSubItem
                        text={t('mobileNavigation.timeline')}
                        link={getLinkUrl(URL_TIMELINE)}
                        onClick={() => onClickItem('timeline')}
                    />
                    <MobileNavigationSubItem
                        className={'border-b border-gray07 dark:border-dark_gray07'}
                        text={t('mobileNavigation.announcements')}
                        link={getLinkUrl(URL_ANNOUNCE)}
                        onClick={() => onClickItem('announce')}
                    />

                    {/* title - features */}
                    <MobileNavigationTitle
                        className={'border-none'}
                        link={getLinkUrl(URL_CHART)}
                        title={t('mobileNavigation.features')}
                        rightIcon={<NewTag />}
                    />
                    <MobileNavigationSubItem
                        text={t('mobileNavigation.chat')}
                        link={getLinkUrl(URL_CHART)}
                        onClick={() => onClickItem('live_chat')}
                    />
                    <MobileNavigationSubItem
                        text={t('mobileNavigation.ai')}
                        link={getLinkUrl(URL_AI)}
                        onClick={() => onClickItem('ai')}
                    />
                    <MobileNavigationSubItem
                        className={'border-b border-gray07 dark:border-dark_gray07'}
                        text={t('mobileNavigation.meme')}
                        link={getLinkUrl(URL_MEME)}
                        onClick={() => onClickItem('meme')}
                    />

                    <MobileNavigationSpace />

                    <MobileNavigationItem
                        text={t('mobileNavigation.currency')}
                        subText={getCurrentCurrencyOption()?.title}
                        onClick={() => {
                            createLog('event', 'navigation_mobile_open_currency_modal')
                            closeModal()
                            changeCurrencyModalRef?.current?.show()
                        }}
                    />
                    <MobileNavigationItem
                        text={t('mobileNavigation.language')}
                        subText={getCurrentLanguage()?.title}
                        onClick={() => {
                            createLog('event', 'navigation_mobile_open_language_modal')
                            closeModal()
                            changeLanguageModalRef?.current?.show()
                        }}
                    />

                    {isCheckDark && (
                        <MobileNavigationItem
                            text={t('mobileNavigation.theme')}
                            subText={
                                isRenderDark ? t('mobileNavigation.theme.dark') : t('mobileNavigation.theme.light')
                            }
                            onClick={() => {
                                createLog('event', 'navigation_mobile_open_theme_modal')
                                closeModal()
                                changeThemeModalRef?.current?.show()
                            }}
                        />
                    )}

                    <div className={'p-[20px] bg-gray07 dark:bg-dark_gray07 grow'}>
                        {isLogin() === true && (
                            <WizButton
                                text={t('mobileNavigation.logout')}
                                buttonType={'border'}
                                className={'w-full'}
                                onClick={onClickLogout}
                            />
                        )}
                    </div>
                </div>
            </NavModal>
            <ChangeCurrencyModal ref={changeCurrencyModalRef} />
            <ChangeLanguageModal ref={changeLanguageModalRef} />
            <ChangeThemeModal ref={changeThemeModalRef} />
        </>
    )
}

export default React.forwardRef(MobileNavigation)
